import { dummyItemTotalsView } from 'checkout/lib/utils/accommodation/view'
import { isVillaItem } from 'lib/checkout/checkoutUtils'
import { isVillaOffer } from 'lib/offer/offerTypes'
import { createSelector } from 'reselect'
import { nonNullable } from 'lib/array/arrayUtils'
import moment from 'moment'
import { DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import { reselectShallowEqual } from 'lib/selector/selectorUtils'
import getLEPackageItemTotals from 'checkout/lib/utils/accommodation/getLEPackageItemTotals'

export const getVillaItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items) => items.filter(isVillaItem),
  reselectShallowEqual,
)

export const getVillaViews = createSelector(
  (state: App.State) => getVillaItems(state),
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.calendar.calendarsByOccupancy,
  (state: App.State) => state.offer.offerAvailableRatesByOccupancy,
  (items, offers, calendars, rates): App.WithDataStatus<Array<App.Checkout.VillaItemView>> => {
    let hasRequiredData = true
    const views = items.map((item): App.Checkout.VillaItemView | undefined => {
      const offer = offers[item.offerId]

      if (!isVillaOffer(offer)) {
        return undefined
      }

      const key = getPackageUniqueKey(item.packageId, item.duration, item.roomRateId)
      const pkg = offer.packages.find(p => p.uniqueKey === key)
      if (!pkg) {
        return undefined
      }

      const itemTotals = getLEPackageItemTotals(
        offer,
        pkg,
        item.checkIn,
        item.checkOut,
        item.occupancy,
        undefined,
        calendars,
        rates,
      )

      hasRequiredData = !!itemTotals

      return {
        title: offer.name,
        totals: itemTotals ?? dummyItemTotalsView(),
        loyalty: {
          productType: getLoyaltyProductType(offer),
        },
        kind: 'villa',
        item,
        package: pkg,
        offer,
      }
    })

    const data = nonNullable(views)

    return {
      hasRequiredData: hasRequiredData && data.length === items.length,
      data,
    }
  },
)

export const getVillaBreakdownView = createSelector(
  (state: App.State) => getVillaViews(state),
  (views): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    const intl = getCurrentIntl()
    const breakdowns = views.data.map((view): App.Checkout.PriceBreakdownView => {
      const occupants = formatOccupantsShort(view.item.occupancy)

      return {
        title: view.offer.name,
        price: view.totals.price,
        memberPrice: view.totals.memberPrice,
        offerType: view.offer.type,
        items: [{
          additionalElements: [],
          additionalInfo: [],
          itemType: 'villa',
          title: '',
          offerId: view.item.offerId,
          itemId: view.item.itemId,
          price: view.totals.price,
          memberPrice: view.totals.memberPrice,
          taxesAndFees: view.totals.taxesAndFees,
          cancellationPolicy: {
            cancellationPolicyDetails: view.package.roomRate?.cancellationPolicy,
            ignoreDynamicCancellationPolicy: view.package.ignoreDynamicCancellationPolicy ?? false,
            useDynamicCancellationPolicies: view.offer?.property?.useDynamicCancellationPolicies ?? false,
            timezoneOffset: view.offer?.property?.timezoneOffset,
            checkInDate: moment(view.item.checkIn),
            checkOutDate: moment(view.item.checkOut),
            occupants: view.item.occupancy,
            offerId: view.item.offerId,
            uniqueKey: view.package.uniqueKey,
          },
        }],
        additionalInfoText: [
          `${moment(view.item.checkIn).format(DMY_CASUAL_SHORT_FORMAT)} → ${moment(view.item.checkOut).format(DMY_CASUAL_SHORT_FORMAT)}`,
          intl.formatMessage(
            {
              id: '6kTYt9aFUGCOL5KFgjXP',
              defaultMessage: '{count, plural, one {# night} other {# nights}} ({occupants})',
              description: 'Night count with occupant summary in villa breakdown info',
            },
            { count: view.item.duration, occupants },
          ),
        ],
        itemsAlwaysVisible: true,
      }
    })
    return {
      hasRequiredData: true,
      data: breakdowns,
    }
  },
)
